import { useMemo } from "react";
import { EntityTypes } from "@vzbl/integrations";
import { useApplicationContext } from "src/hooks/useApplication";
import { useEntityContext } from "../useEntityContext";
import { VzblTag } from "../components/VzblTag";
import { TabbedWork } from "../components/TabbedWork";
import { Work, WorkComponent } from "../Work";
import { Insights } from "../Insights";
import { TimeMachine, TimeMachineComponent } from "../TimeMachine";
import { useWorkTabs } from "../components/worktabs";
import { EntityScreen } from "./components/layout/Screen";
import { HeaderPanel } from "./components/layout/HeaderPanel";
import { ActionCard } from "./components/ActionCard";
import { MetricTag, SummaryTag } from "./components/Tags";
import { SummaryPanel } from "./components/layout/SummaryPanel";
import { InsightsPanel } from "./components/layout/InsightsPanel";
import { toHeaderAttributes } from "./components/HeaderAttributes";
import { EntityTabs } from "./components/EntityTabs";
import { ProgressMetric } from "./components/ProgressMetric";
import { SentencesList } from "./components/SentencesList";

export const PullRequestEntity = () => {
  const context = useEntityContext();
  const current = context.dataRef.current.entity;
  return (
    <EntityScreen
      headerPanel={
        <HeaderPanel
          entity={current}
          label={current.properties.title}
          attributes={usePullRequestHeaderAttributes(current)}
        />
      }
      summaryPanel={
        <SummaryPanel
          title={current.properties.title}
          description={current.properties.vzblSummaryChangeset}
        />
      }
      insightsPanel={<InsightsPanel cards={usePullRequestInsights(current)} />}
    >
      <EntityTabs
        propertiesSkipList={[
          "repository",
          "branch",
          "commits",
          "name",
          "number",
          "diffUrl",
          "issueUrl",
          "reviewCommentUrl",
          "title",
          "vzblRiskScore",
          "vzblRiskExplanation",
          "vzblClassification",
          "vzblSummary",
        ]}
        entity={current}
        tabs={usePullRequestTabs(current)}
      />
    </EntityScreen>
  );
};

export function usePullRequestHeaderAttributes(entity: any) {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entityType = entity.labels[0];
  return useMemo(
    () =>
      toHeaderAttributes({
        orgId,
        context,
        entityType,
        attributes: [
          { name: "repository", text: entity.properties.repository },
          { name: "branch", text: entity.properties.branch },
          { name: "commits", text: entity.properties.commits },
        ],
      }),
    [context, orgId, entityType, entity]
  );
}

export function usePullRequestTabs(entity: any) {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  return [
    {
      key: "overview",
      id: "overview",
      label: "Overview",
      children: <Insights entity={context.dataRef.current.entity} />,
    },
    {
      key: "2",
      id: "2",
      label: "Work",
      children: (
        <>
          <TabbedWork
            items={useWorkTabs(orgId, entity.properties.uuid, [
              { entityTypes: EntityTypes.Issue },
              { entityTypes: EntityTypes.Sprint },
              { entityTypes: EntityTypes.Commit },
              {
                entityTypes: EntityTypes.PullRequestReviewComment,
                label: "Comments",
              },
              { entityTypes: EntityTypes.Branch, label: "Branches" },
              { entityTypes: EntityTypes.Release },
              { entityTypes: EntityTypes.Repository, label: "Repositories" },
              { entityTypes: EntityTypes.CicdPipeline, label: "Deployments" },
            ])}
          />
          <Work
            orgId={orgId}
            entityUuid={entity.properties.uuid}
            types={["Issue"]}
          >
            <WorkComponent title="Related Issues" />
          </Work>
        </>
      ),
    },

    {
      key: "5",
      id: "5",
      label: "History",
      children: (
        <TimeMachine>
          <TimeMachineComponent />
        </TimeMachine>
      ),
    },
  ];
}

export function usePullRequestInsights(entity: any) {
  const orgId = useApplicationContext().org_id!;
  const context = useEntityContext();
  const entityType = entity.labels[0];

  const {
    vzblSummary, //legacy, replaced by purpose and changeset
    vzblSummaryPurpose,
    vzblAtomicity,
    vzblAtomicityExplanation,
    vzblMaintainability,
    vzblMaintainabilityExplanation,
    vzblQuality,
    vzblQualityExplanation,
    vzblComplexity,
    vzblComplexityExplanation,
    vzblRiskScore,
    vzblRiskScoreExplanation,
    vzblTechStackLayers,
    vzblTechStackLayerExplanations,
    vzblClassification,
    vzblClassificationExplanation,
  } = entity.properties;

  return [
    vzblMaintainability ? (
      <ActionCard
        key="maintainability"
        badge={<MetricTag />}
        label="Maintainability"
        value={
          <ProgressMetric
            higherBetter
            value={vzblMaintainability}
            tooltip={vzblMaintainabilityExplanation}
          />
        }
      />
    ) : undefined,
    vzblQuality ? (
      <ActionCard
        key="quality"
        badge={<MetricTag />}
        label="Quality"
        value={
          <ProgressMetric
            higherBetter
            value={vzblQuality}
            tooltip={vzblQualityExplanation}
          />
        }
      />
    ) : undefined,
    vzblComplexity ? (
      <ActionCard
        key="complexity"
        badge={<MetricTag />}
        label="Complexity"
        value={
          <ProgressMetric
            lowerBetter
            value={vzblComplexity}
            tooltip={vzblComplexityExplanation}
          />
        }
      />
    ) : undefined,
    vzblAtomicity ? (
      <ActionCard
        key="atomicity"
        badge={<MetricTag />}
        label="Atomicity"
        value={
          <ProgressMetric
            lowerBetter
            value={vzblAtomicity}
            data={vzblAtomicityExplanation}
          />
        }
      />
    ) : undefined,
    vzblRiskScore ? (
      <ActionCard
        key="risk-score"
        badge={<MetricTag />}
        label="Risk Score"
        value={
          <ProgressMetric
            lowerBetter
            value={vzblRiskScore}
            data={vzblRiskScoreExplanation}
          />
        }
      />
    ) : undefined,
    vzblClassification !== undefined && (
      <ActionCard
        key="classification"
        badge={<MetricTag />}
        label="Classification"
        value={
          <div>
            <VzblTag
              orgId={orgId}
              context={context}
              text={vzblClassification}
              color="none"
              linkParams={{
                orgId,
                entityType: entityType,
                attribute: "vzblClassification",
                value: vzblClassification,
              }}
            />
            <SentencesList data={vzblClassificationExplanation} />
          </div>
        }
      />
    ),
    vzblTechStackLayers !== undefined && (
      <ActionCard
        key="techstack"
        badge={<MetricTag />}
        label="Tech Stack Layers"
        value={
          <div>
            <VzblTag
              orgId={orgId}
              context={context}
              text={vzblTechStackLayers}
              color="none"
              linkParams={{
                orgId,
                entityType: entityType,
                attribute: "vzblTechStackLayers",
                value: vzblTechStackLayers,
              }}
            />
            <SentencesList data={vzblTechStackLayerExplanations} />
          </div>
        }
      />
    ),
    (vzblSummaryPurpose || vzblSummary) && (
      <ActionCard
        key="vzbl-summary"
        badge={<SummaryTag />}
        label="Summary"
        value={
          <SentencesList showNa data={vzblSummaryPurpose || vzblSummary} />
        }
      />
    ),
  ].filter(Boolean);
}
